import { useEffect, useState } from "react";
import {
  getModel,
  setModel,
  getDisplayName,
  setDisplayName,
  getReasoning,
  setReasoning,
  type ReasoningLevel,
} from "../lib/store";
import * as api from "../lib/api";
import type { MeResult, CreditsResult, BillingMode } from "../lib/api";

// Latest Claude model IDs as of 2026-05 — see
// https://platform.claude.com/docs/en/docs/about-claude/models/overview
const MODELS = [
  { value: "claude-sonnet-4-6", label: "Sonnet 4.6 — recommended (balanced)" },
  { value: "claude-opus-4-8", label: "Opus 4.8 — smartest, slower & most expensive" },
  { value: "claude-opus-4-7", label: "Opus 4.7 — older, cheaper than 4.8" },
  { value: "claude-haiku-4-5", label: "Haiku 4.5 — cheapest, fastest" },
  { value: "kimi-k3", label: "Kimi K3 — experimental (Opus-class, Sonnet price)" },
];

const REASONING_OPTIONS: { value: ReasoningLevel; label: string; hint: string }[] = [
  {
    value: "off",
    label: "Off",
    hint: "Fastest, cheapest option, struggles with complex projects. Good for simple dice rollers and utility apps, will likely struggle with complex engineering.",
  },
  {
    value: "low",
    label: "Low",
    hint: "Fast and cheap, not good with complex projects. If you're getting stuck, try increasing this.",
  },
  {
    value: "medium",
    label: "Medium",
    hint: "Recommended for most projects. Good balance of cost and performance.",
  },
  {
    value: "high",
    label: "High",
    hint: "Most expensive and slowest, but most intelligent. Good for highly complex projects or fixing hard bugs.",
  },
];

const MODEL_LABEL: Record<string, string> = Object.fromEntries(
  MODELS.map((m) => [m.value, m.label.split(" — ")[0] ?? m.label])
);

/** The auto-top-up threshold floor (USD) for a model — the worst-case cost of one build step.
 *  Mirrors the server's stepCostUsdByModel; falls back to the priciest known model. */
function thresholdFloorUsd(stepCosts: Record<string, number>, model: string): number {
  const cost = stepCosts[model] ?? Math.max(1, ...Object.values(stepCosts));
  return Math.max(1, Math.ceil(cost));
}

export function Settings({
  onClose,
  required,
  me,
  refreshMe,
  notify,
}: {
  onClose: () => void;
  required?: boolean;
  me: MeResult | null;
  refreshMe: () => Promise<void>;
  /** Show a prominent, must-dismiss notice (used when a model change raises the billing trigger). */
  notify?: (msg: string) => void;
}) {
  const signedIn = !!me?.user;
  const hasKey = !!me?.hasApiKey;
  // Local mirror of the server's billingMode, so the UI flips instantly on toggle while the
  // PUT round-trips. The server's value is authoritative — we reconcile after refreshMe.
  const [billingMode, setBillingModeState] = useState<BillingMode>(me?.billingMode ?? "credits");
  useEffect(() => {
    if (me?.billingMode) setBillingModeState(me.billingMode);
  }, [me?.billingMode]);
  // Blank = leave the stored key unchanged. We never prefill — the server never returns it.
  const [apiKey, setKey] = useState("");
  // Model / reasoning / display name are local (non-secret) prefs — saved to localStorage
  // LIVE as they change, so the rest of the UI (and the auto-top-up floor) reacts immediately
  // instead of waiting for a Save the user might not realize they need.
  const [model, setModelState] = useState(getModel());
  const [reasoning, setReasoningState] = useState<ReasoningLevel>(getReasoning());
  const [name, setName] = useState(getDisplayName());
  const [saving, setSaving] = useState(false);
  const [keyMsg, setKeyMsg] = useState<{ ok: boolean; msg: string } | null>(null);

  // ----- Credits (Tier 1.5D) -----
  const [credits, setCredits] = useState<CreditsResult | null>(null);
  const [creditsBusy, setCreditsBusy] = useState(false);
  // Top-up amount (the one real preference here), seeded once from the server.
  const [autoAmt, setAutoAmt] = useState<number | null>(null);

  useEffect(() => {
    if (!signedIn) return;
    api.getAccountCredits().then((c) => {
      setCredits(c);
      setAutoAmt((p) => (p === null ? c.autoTopupAmountUsd : p));
    }).catch(() => {});
  }, [signedIn]);

  // The auto-top-up TRIGGER is computed, not chosen: it's the worst-case cost of one build step
  // on the CURRENTLY selected model. Locked + shown bolded. Tracks the live dropdown.
  const triggerUsd = credits ? thresholdFloorUsd(credits.stepCostUsdByModel, model) : 1;
  const MIN_TOPUP = api.TOPUP_PRESETS_USD[0] ?? 10;
  // Auto-top-up amount options: the presets, plus the current value if it's off-preset.
  const amountOptions = Array.from(
    new Set<number>([...api.TOPUP_PRESETS_USD, ...(autoAmt != null ? [autoAmt] : [])])
  ).sort((a, b) => a - b);

  async function onBuy(amountUsd: number) {
    setCreditsBusy(true);
    try {
      const { url } = await api.startTopupCheckout(amountUsd);
      window.location.href = url; // hosted Stripe Checkout
    } catch (e) {
      setKeyMsg({ ok: false, msg: (e as Error).message });
      setCreditsBusy(false);
    }
  }
  async function saveAuto(patch: { autoTopup?: boolean; autoTopupAmountUsd?: number }) {
    if (!credits) return;
    setCreditsBusy(true);
    setKeyMsg(null);
    try {
      const next = await api.setAutoTopup({ autoTopup: patch.autoTopup ?? credits.autoTopup, ...patch });
      setCredits(next);
      setAutoAmt(next.autoTopupAmountUsd);
    } catch (e) {
      setKeyMsg({ ok: false, msg: (e as Error).message });
    } finally {
      setCreditsBusy(false);
    }
  }

  // Model change is LIVE (persisted immediately). The auto-top-up trigger is derived from the
  // model, so it moves the instant you pick one — nothing to persist. We only ALERT when the
  // trigger goes UP (pricier model → card charged sooner); a cheaper model lowers it silently
  // (strictly fewer billing events, so no warning needed).
  function onModelChange(next: string) {
    if (credits && credits.autoTopup) {
      const before = thresholdFloorUsd(credits.stepCostUsdByModel, model);
      const after = thresholdFloorUsd(credits.stepCostUsdByModel, next);
      if (after > before) {
        notify?.(
          `${MODEL_LABEL[next] ?? next}: auto-top-up now triggers at $${after.toFixed(0)} (was $${before.toFixed(0)}), ` +
            `since one ${MODEL_LABEL[next] ?? next} step can cost that much. Your card may be charged a bit sooner.`
        );
      }
    }
    setModelState(next);
    setModel(next); // localStorage, live
  }

  async function onSave() {
    // Prefs / auto-top-up / billing-mode all persist live; Save's only remaining job is to
    // commit a typed API key in BYO mode and close. Two paths, both gated on BYO mode:
    //   - typed something → PUT (replace / set)
    //   - cleared the field while a key is on file → DELETE (with confirm — destructive).
    // In credits mode the input doesn't exist, apiKey is always "", and we never touch the
    // stored key (it stays dormant on disk in case the user flips back to BYO).
    setSaving(true);
    setKeyMsg(null);
    try {
      if (billingMode === "byo") {
        if (apiKey.trim()) {
          await api.putAccountKey(apiKey.trim());
          setKey("");
          await refreshMe();
        } else if (hasKey) {
          const ok = window.confirm(
            "Remove your stored Anthropic API key? You'll need to re-add one (or switch to credits) before you can build."
          );
          if (!ok) {
            setSaving(false);
            return;
          }
          await api.deleteAccountKey();
          await refreshMe();
        }
      }
      onClose();
    } catch (e) {
      setKeyMsg({ ok: false, msg: (e as Error).message });
      setSaving(false); // stay open so they can fix the key
    }
  }

  async function onRemoveKey() {
    setSaving(true);
    setKeyMsg(null);
    try {
      await api.deleteAccountKey();
      await refreshMe();
      setKeyMsg({ ok: true, msg: "key removed" });
    } catch (e) {
      setKeyMsg({ ok: false, msg: (e as Error).message });
    } finally {
      setSaving(false);
    }
  }

  // The Studio "required" flow demands a working billing path before Save can dismiss the
  // modal. The exact requirement depends on the current mode:
  //   - 'byo'     → a key must be on file or typed in
  //   - 'credits' → a positive balance (the user tops up via Stripe; Save is fine to
  //                 dismiss if balance > 0). They can also just flip to BYO if they have
  //                 an Anthropic key — the toggle's instant.
  const blockedForSetup =
    Boolean(required) &&
    (billingMode === "byo"
      ? !hasKey && !apiKey.trim()
      : !((credits?.balanceUsd ?? me?.creditBalanceUsd ?? 0) > 0));

  // Switch billing path. Persisted live (no Save needed) so the toggle is the only thing
  // the user has to think about. The stored API key (if any) stays on disk regardless of
  // mode — switching to 'credits' just makes it dormant; switching back resumes using it.
  async function onPickMode(next: BillingMode) {
    if (next === billingMode) return;
    setBillingModeState(next); // optimistic
    setKeyMsg(null);
    try {
      await api.setBillingMode(next);
      await refreshMe();
    } catch (e) {
      // Roll back if the server rejected it.
      setBillingModeState(billingMode);
      setKeyMsg({ ok: false, msg: (e as Error).message });
    }
  }

  return (
    <div className="modal-bg">
      <div className="modal">
        <h2>Settings</h2>

        {/* Signed-out shell: nothing to configure yet — explain and offer sign-in. */}
        {!signedIn && (
          <div className="row">
            <div className="hint" style={{ marginBottom: 8 }}>
              Sign in to set up billing. You can either buy credits (we run builds for you)
              or paste your own Anthropic API key (you pay Anthropic directly).
            </div>
            <button className="primary" onClick={() => (window.location.href = api.googleSignInUrl())}>
              Sign in with Google
            </button>
          </div>
        )}

        {/* Billing-mode toggle — segmented pill at the top of the modal. Explicit user
            choice; persisted live on click so the rest of the modal flips immediately.
            The non-selected mode's section is hidden entirely (not greyed out) — that
            sidesteps the "did Chrome autofill my key into the credits view?" ambiguity. */}
        {signedIn && (
          <div className="row">
            <div className="label">Pay with</div>
            <div
              role="radiogroup"
              aria-label="Billing mode"
              style={{
                display: "inline-flex",
                gap: 0,
                border: "1px solid var(--border-2)",
                borderRadius: 8,
                overflow: "hidden",
                background: "var(--bg)",
              }}
            >
              {([
                { value: "credits", label: "Credits", hint: "We run your builds" },
                { value: "byo", label: "My Anthropic key", hint: "You pay Anthropic directly" },
              ] as const).map((opt, i) => {
                const selected = billingMode === opt.value;
                return (
                  <button
                    key={opt.value}
                    role="radio"
                    aria-checked={selected}
                    onClick={() => void onPickMode(opt.value)}
                    style={{
                      padding: "8px 14px",
                      background: selected ? "var(--accent)" : "transparent",
                      color: selected ? "#0b0e14" : "var(--fg)",
                      border: "none",
                      borderLeft: i === 0 ? "none" : "1px solid var(--border-2)",
                      fontSize: 13,
                      fontWeight: selected ? 600 : 400,
                      cursor: "pointer",
                    }}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* API-key section — only shown in BYO mode. In credits mode the field doesn't
            exist in the DOM at all (so the password manager has nothing to autofill into). */}
        {signedIn && billingMode === "byo" && (
          <div className="row">
            <div className="label">Anthropic API key</div>
            {hasKey && (
              <div className="hint" style={{ marginBottom: 6 }}>
                <span className="status-ok mono">✓ a key is on file</span> — paste a new one
                to replace it, or save with the field empty to remove it.{" "}
                <button
                  className="btn-ghost"
                  onClick={onRemoveKey}
                  disabled={saving}
                  style={{ marginLeft: 4, fontSize: 12 }}
                >
                  Remove key
                </button>
              </div>
            )}
            {/* Aggressively block browser/password-manager autofill: API keys are stored
                server-side encrypted, the browser never needs to remember them. The
                readonly-until-focus trick is the only reliable defense against Chrome's
                autofill on type="password" (which IGNORES autoComplete="off"). */}
            <input
              type="password"
              placeholder={hasKey ? "paste a new key to replace…" : "sk-ant-..."}
              value={apiKey}
              onChange={(e) => setKey(e.target.value)}
              readOnly
              onFocus={(e) => e.currentTarget.removeAttribute("readonly")}
              autoComplete="off"
              name="quikpl-anthropic-key"
              data-lpignore="true"
              data-1p-ignore="true"
              data-form-type="other"
            />
            <div className="hint">
              Your key is encrypted at rest on our server and used to run builds on your
              behalf — so a build keeps going even if you close the tab.
            </div>
          </div>
        )}

        {/* Status message — lives outside the mode-specific sections so toggle errors,
            key-save errors, and Stripe-checkout errors all surface in the same spot. */}
        {keyMsg && (
          <div
            className={"mono " + (keyMsg.ok ? "status-ok" : "status-err")}
            style={{ marginTop: 6, fontSize: 12 }}
          >
            {keyMsg.ok ? "✓ " : "✗ "}
            {keyMsg.msg}
          </div>
        )}

        {/* Credits section — only shown in credits mode. */}
        {signedIn && billingMode === "credits" && (
          <div className="row">
            <div className="label">Credits</div>
            <div className="hint" style={{ marginBottom: 8 }}>
              No API key or don't know what that is? Just buy some credits and get started
              (simplest option for most people). Credits are billed at cost + 5% + payment
              processing fees.
            </div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginBottom: 10 }}>
              <span style={{ fontSize: 22, fontWeight: 600 }}>
                ${credits ? credits.balanceUsd.toFixed(2) : (me?.creditBalanceUsd ?? 0).toFixed(2)}
              </span>
              {credits && (
                <span className="mono" style={{ fontSize: 11, color: "var(--fg-faint)" }}>
                  spent: ${credits.spend.todayUsd.toFixed(2)} today · ${credits.spend.weekUsd.toFixed(2)} wk · ${credits.spend.totalUsd.toFixed(2)} total
                </span>
              )}
            </div>
            {/* Buy buttons + the auto-top-up toggle on one row (toggle pushed to the right).
                The checkbox keeps its natural size now (see styles.css), and the label is
                flex-shrink:0 + nowrap, so it never wraps.
                Per-user balance cap: any preset whose amount would push past maxBalanceUsd
                is disabled (server enforces too — this is UX). */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
              {api.TOPUP_PRESETS_USD.map((amt) => {
                const overCap = credits ? credits.balanceUsd + amt > credits.maxBalanceUsd : false;
                return (
                  <button
                    key={amt}
                    onClick={() => onBuy(amt)}
                    disabled={creditsBusy || overCap}
                    title={overCap ? `Would exceed the $${credits!.maxBalanceUsd} balance cap` : undefined}
                  >
                    Add ${amt}
                  </button>
                );
              })}
              <label
                style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, marginLeft: "auto", flexShrink: 0, whiteSpace: "nowrap" }}
              >
                <input
                  type="checkbox"
                  checked={credits ? credits.autoTopup : true}
                  disabled={creditsBusy || !credits}
                  onChange={(e) => saveAuto({ autoTopup: e.target.checked })}
                />
                Auto top-up
              </label>
            </div>
            {credits && credits.balanceUsd + api.TOPUP_PRESETS_USD[0]! > credits.maxBalanceUsd && (
              <div className="hint" style={{ marginBottom: 8 }}>
                You're at the per-user balance cap (${credits.maxBalanceUsd}). Spend some
                credit before topping up again.
              </div>
            )}
            {/* Top-up amount is the only choice (a content-sized <select>, so it flows in the
                sentence). The trigger is computed from the model — shown bolded + locked. */}
            {credits && credits.autoTopup && (
              <>
                <div className="hint" style={{ lineHeight: 1.8 }}>
                  Top up{" "}
                  <select
                    value={autoAmt ?? MIN_TOPUP}
                    disabled={creditsBusy}
                    onChange={(e) => {
                      const v = Number(e.target.value);
                      setAutoAmt(v);
                      saveAuto({ autoTopupAmountUsd: v });
                    }}
                    style={{ padding: "1px 4px", margin: "0 2px" }}
                  >
                    {amountOptions.map((a) => (
                      <option key={a} value={a}>${a}</option>
                    ))}
                  </select>{" "}
                  in credits when my balance drops below{" "}
                  <b data-testid="autotopup-threshold">${triggerUsd}</b>.
                </div>
                <div className="hint" style={{ marginTop: 4 }}>
                  Trigger set by your model to avoid interruptions.
                </div>
              </>
            )}
          </div>
        )}

        <div className="row">
          <div className="label">Model</div>
          <select
            data-testid="model-select"
            value={model}
            onChange={(e) => void onModelChange(e.target.value)}
            style={{
              width: "100%",
              padding: "8px 10px",
              background: "var(--panel)",
              color: "var(--fg)",
              border: "1px solid var(--border-2)",
              borderRadius: 6,
              fontSize: 13,
            }}
          >
            {MODELS.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </div>
        <div className="row">
          <div className="label">Reasoning effort</div>
          <select
            value={reasoning}
            onChange={(e) => {
              const r = e.target.value as ReasoningLevel;
              setReasoningState(r);
              setReasoning(r); // localStorage, live
            }}
            style={{
              width: "100%",
              padding: "8px 10px",
              background: "var(--panel)",
              color: "var(--fg)",
              border: "1px solid var(--border-2)",
              borderRadius: 6,
              fontSize: 13,
            }}
          >
            {REASONING_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <div className="hint">
            {REASONING_OPTIONS.find((o) => o.value === reasoning)?.hint}
            {" "}Extended thinking lets the model plan before acting — bigger budget =
            better at complex builds but more tokens billed at the output rate.
          </div>
        </div>
        <div className="row">
          <div className="label">Display name (optional)</div>
          <input
            placeholder="Atticus"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => setDisplayName(name.trim())}
          />
        </div>

        <div className="actions" style={{ justifyContent: "flex-end" }}>
          <div style={{ display: "flex", gap: 8 }}>
            {!required && <button onClick={onClose}>Cancel</button>}
            <button className="primary" onClick={onSave} disabled={saving || blockedForSetup}>
              {saving ? <><span className="spinner" /> Saving…</> : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
